import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { admin, inputCls } from "./adminApi";

const EMPTY = { code: "", percent: 10, label: "", expires_at: "", max_uses: "" };

function promoStatus(p) {
  if (!p.active) return "paused";
  if (p.expires_at && new Date(p.expires_at) < new Date()) return "expired";
  if (p.max_uses && p.uses >= p.max_uses) return "exhausted";
  return "live";
}

const BADGE = {
  live:      { c: "text-[color:var(--bl-green)] border-[color:var(--bl-green)]/40", t: "Live" },
  paused:    { c: "text-white/50 border-white/20", t: "Paused" },
  expired:   { c: "text-red-300 border-red-400/40", t: "Expired" },
  exhausted: { c: "text-[color:var(--bl-gold)] border-[color:var(--bl-gold)]/40", t: "Exhausted" },
};

const Field = ({ label, children }) => (
  <div><div className="bl-eyebrow text-white/60 mb-2">{label}</div>{children}</div>
);

function PromoForm({ onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm(p => ({ ...p, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error("Code required"); return; }
    setBusy(true);
    try {
      await admin.post(`/admin/promos`, {
        code: form.code.trim(), percent: Number(form.percent), label: form.label.trim() || undefined, active: true,
        expires_at: form.expires_at || undefined, max_uses: form.max_uses ? Number(form.max_uses) : undefined,
      });
      toast.success("Promo created");
      setForm(EMPTY);
      await onCreated();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="border border-white/8 p-5 grid md:grid-cols-[1fr,90px,1.5fr,140px,110px,auto] gap-3 items-end">
      <Field label="Code">
        <input data-testid="promo-code" value={form.code} onChange={e => set({ code: e.target.value.toUpperCase() })} placeholder="FLASH20"
          className={`w-full ${inputCls} px-3 py-2.5 text-sm uppercase`} />
      </Field>
      <Field label="% off">
        <input type="number" min="1" max="90" data-testid="promo-percent" value={form.percent} onChange={e => set({ percent: e.target.value })}
          className={`w-full ${inputCls} px-3 py-2.5 text-sm text-right`} />
      </Field>
      <Field label="Label (optional)">
        <input data-testid="promo-label" value={form.label} onChange={e => set({ label: e.target.value })} placeholder="Flash Sale · 20% off"
          className={`w-full ${inputCls} px-3 py-2.5 text-xs`} />
      </Field>
      <Field label="Expires">
        <input type="date" data-testid="promo-expires" value={form.expires_at ? form.expires_at.slice(0, 10) : ""}
          onChange={e => set({ expires_at: e.target.value ? new Date(e.target.value + "T23:59:59Z").toISOString() : "" })}
          className={`w-full ${inputCls} px-3 py-2.5 text-xs`} />
      </Field>
      <Field label="Max uses">
        <input type="number" min="1" data-testid="promo-max-uses" value={form.max_uses} onChange={e => set({ max_uses: e.target.value })} placeholder="∞"
          className={`w-full ${inputCls} px-3 py-2.5 text-xs text-right`} />
      </Field>
      <button type="submit" disabled={busy} data-testid="promo-create" className="bl-btn gold text-[11px] py-2.5 px-4 inline-flex items-center gap-2">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <><Plus size={13} /> Create</>}
      </button>
    </form>
  );
}

function PromoRow({ p, onToggle, onDelete }) {
  const badge = BADGE[promoStatus(p)];
  return (
    <div data-testid={`promo-row-${p.code}`} className="p-5 flex flex-wrap items-center gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="font-display text-white text-lg tracking-wider">{p.code}</div>
          <span className="text-[color:var(--bl-gold)] font-mono text-sm">{p.percent}% off</span>
          <span className={`font-mono text-[10px] tracking-[0.22em] uppercase border px-2 py-0.5 ${badge.c}`}>{badge.t}</span>
          {p.source === "default" && <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40 border border-white/15 px-2 py-0.5">Seed</span>}
        </div>
        <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-white/40 mt-2 flex flex-wrap gap-3">
          <span>{p.label}</span>
          <span>Uses: {p.uses}{p.max_uses ? ` / ${p.max_uses}` : ""}</span>
          {p.expires_at && <span>Expires: {new Date(p.expires_at).toLocaleDateString()}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={() => onToggle(p.code, !p.active)} data-testid={`promo-toggle-${p.code}`} className={`bl-btn text-[10px] py-2 px-3 ${p.active ? "" : "gold"}`}>
          {p.active ? "Pause" : "Activate"}
        </button>
        {p.source !== "default" && (
          <button onClick={() => onDelete(p.code)} data-testid={`promo-delete-${p.code}`}
            className="bl-btn text-[10px] py-2 px-3 inline-flex items-center gap-1 text-red-300 border-red-400/30 hover:border-red-400/60"><Trash2 size={11} /></button>
        )}
      </div>
    </div>
  );
}

export default function AdminPromos({ onUnauthed }) {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { setPromos((await admin.get(`/admin/promos`)).data.promos); }
    catch (err) { if (err?.response?.status === 401) onUnauthed(); }
    finally { setLoading(false); }
  }, [onUnauthed]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (code, active) => {
    try { await admin.patch(`/admin/promos/${code}`, { active }); await load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (code) => {
    if (!window.confirm(`Delete promo ${code}?`)) return;
    try { await admin.delete(`/admin/promos/${code}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-promos-panel">
      <PromoForm onCreated={load} />
      {loading ? (
        <div className="py-12 text-center"><Loader2 className="animate-spin text-white/40 mx-auto" /></div>
      ) : promos.length === 0 ? (
        <div className="text-white/40 py-8 text-center">No promo codes yet</div>
      ) : (
        <div className="border border-white/8 divide-y divide-white/8">
          {promos.map((p) => <PromoRow key={p.code} p={p} onToggle={toggle} onDelete={remove} />)}
        </div>
      )}
    </div>
  );
}
